import * as React from "react";
import { useTranslation } from "react-i18next";
import {
  DataGrid,
  type GridColDef,
  type GridRowSelectionModel,
  type GridCallbackDetails,
} from "@mui/x-data-grid";
import {
  Box,
  Menu,
  MenuItem as MuiMenuItem,
  IconButton,
  CircularProgress,
  Typography,
  Grid,
  Button,
  InputAdornment,
  TextField,
  FormControl,
  Select,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import MiniLoader from "../MiniLoader";
import { t } from "i18next";

export interface CommonDataGridAction {
  label: string;
  onClick: (row: any) => void;
  disabled?: boolean;
}

export interface CommonDataGridProps {
  columns: GridColDef[];
  rows: any[];
  totalRowCount?: number;
  loading?: boolean;
  error?: string | null;
  noRowsMessage?: string;
  checkboxSelection?: boolean;
  selectedCartCount?: number;
  customActionEvent?: any;
  customActionEventTitle?: any;
  searchPlaceholderText?: any;
  enableSearch?: boolean;
  pageSizeOptions?: number[];
  paginationModel?: { page: number; pageSize: number };
  paginationMode?: "client" | "server";
  sortingMode?: "client" | "server";
  pageName?: string;
  hideFooter?: boolean;
  showPaginationDropdown?: Boolean
  onRowSelect?: (
    selectedRows: GridRowSelectionModel,
    details: GridCallbackDetails
  ) => void;
  onPaginationModelChange?: (
    model: { page: number; pageSize: number },
    details: GridCallbackDetails
  ) => void;
  actionsColumn?: {
    headerName?: string;
    actions: (row: any) => CommonDataGridAction[];
    width?: number;
  };
  sx?: any;
  onFilterModelChange?: (filterModel: any) => void;
  hideHeaderCount?: boolean;
  sortField?: string | null;
  sortDirection?: 'asc' | 'desc' | null;
  onSortChange?: (field: string | null, direction: 'asc' | 'desc' | null) => void;
  customHeightEnable?: boolean,
  showCustomActionEventLoader?: boolean;
  childItemKey?: string;
  hideFooterEntries?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
}

const CommonDataGrid: React.FC<CommonDataGridProps> = (props) => {
  // Generic data grid component for displaying tabular data with pagination, sorting, and actions
  const { t } = useTranslation();
  const {
    columns,
    rows,
    totalRowCount,
    loading = false,
    noRowsMessage = "",
    checkboxSelection = false,
    pageSizeOptions = [10, 25, 50, 100], // Default page size options
    paginationModel = { page: 0, pageSize: 25 },
    paginationMode = "server", // Default to server-side pagination
    sortingMode = "client",
    hideFooter = false,
    onRowSelect,
    onPaginationModelChange,
    actionsColumn,
    selectedCartCount = 0,
    customActionEvent = null,
    customActionEventTitle = null,
    pageName = "",
    enableSearch = false,
    searchPlaceholderText = "Search...",
    hideHeaderCount = false,
    showPaginationDropdown = false,
    customHeightEnable=false,
    hideFooterEntries = false,
    childItemKey=null,
    showCustomActionEventLoader=false,
    searchValue,
    onSearchChange,

  } = props;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = React.useState<any>(null);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [expandedRows, setExpandedRows] = React.useState<Set<string | number>>(new Set());

  // For server-side pagination, use totalRowCount if available, otherwise use rows.length
  const effectiveRowCount = paginationMode === "server" ? (totalRowCount || rows.length) : rows.length;
  const displayRows = paginationMode === "server" ? rows : rows; // For server mode, use rows as-is

    const processRowsWithChildren = React.useCallback((rowsData: any[]) => {
    if (!childItemKey) return rowsData;

    const processedRows: any[] = [];

    rowsData.forEach((row) => {
      // Add parent row
      processedRows.push({
        ...row,
        isParent: row[childItemKey] && Array.isArray(row[childItemKey]) && row[childItemKey].length > 0
      });

      // Add child rows if parent is expanded and has children
      if (row[childItemKey] && Array.isArray(row[childItemKey]) && row[childItemKey].length > 0) {
        const rowId = row?.id || row?._id || row?.key || row?.order;
        if (expandedRows.has(rowId)) {
          row[childItemKey].forEach((childRow: any, index: number) => {
            processedRows.push({
              ...childRow,
              id: `${rowId}_child_${index}`,
              isChild: true,
              parentId: rowId
            });
          });
        }
      }
    });

    return processedRows;
  }, [childItemKey, expandedRows]);

  const toggleRowExpansion = (rowId: string | number) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  const enhancedColumns = React.useMemo(() => {
    let newColumns = [...columns];

    // Add expand column if childItemKey is provided
    if (childItemKey) {
      const expandColumn: GridColDef = {
        field: "__expand__",
        headerName: "",
        width: 50,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params: any) => {
          if (params.row.isChild) {
            return <Box sx={{ width: 24, height: 24 }} />; // Empty space for child rows
          }

          if (!params.row.isParent) {
            return <Box sx={{ width: 24, height: 24 }} />; // Empty space for rows without children
          }

          const rowId = params.row?.id || params.row?._id || params.row?.key || params.row?.order;
          const isExpanded = expandedRows.has(rowId);

          return (
            <IconButton
              size="small"
              onClick={() => toggleRowExpansion(rowId)}
              className="common-data-grid-expand-icon"
            >
              {isExpanded ? (
                <KeyboardArrowDownIcon  fontSize="small" />
              ) : (
                <KeyboardArrowRightIcon  fontSize="small" />
              )}
            </IconButton>
          );
        },
      };
      newColumns = [expandColumn, ...newColumns];
    }

    // Add actions column if actionsColumn is provided
    if (actionsColumn) {
      newColumns.push({
        field: "__actions__",
        headerName: actionsColumn.headerName || t("actions", "ACTIONS"),
        width: 100,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        disableColumnMenu: true,
        renderCell: (params: any) => (
          <>
            <IconButton
              size="small"
              onClick={(e) => {
                setAnchorEl(e.currentTarget);
                setMenuRow(params.row);
              }}
              className="common-data-grid-actions-icon"
              sx={{
                outline: "none",
                boxShadow: "none",
                "&:focus": { outline: "none", boxShadow: "none" },
                "&:active": { outline: "none", boxShadow: "none" },
                // Generated by Copilot
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl) && menuRow === params.row}
              onClose={() => setAnchorEl(null)}
              disableScrollLock={true}
              PaperProps={{ className: "common-data-grid-actions-menu" }}
            >
              <Typography className="common-data-grid-actions-menu-title">
                {t("actions", "ACTIONS")}
              </Typography>
                {actionsColumn.actions(params.row).map((action, idx) => (
                <MuiMenuItem
                  key={idx}
                  onClick={() => {
                    action.onClick(params.row);
                    setAnchorEl(null);
                  }}
                  disabled={action.disabled}
                  className="common-data-grid-actions-menu-item"
                >
                  {action.label}
                </MuiMenuItem>
              ))}
            </Menu>
          </>
        ),
      } as GridColDef);
    }

    return newColumns;
  }, [columns, actionsColumn, anchorEl, menuRow, childItemKey, expandedRows, toggleRowExpansion, t]);

  const handlePageSizeChange = (event: any) => {
    if (onPaginationModelChange && paginationModel) {
      onPaginationModelChange({ ...paginationModel, pageSize: event.target.value as number, page: 0 }, { reason: "pageSizeChange" } as any)
    }
  }

  const normalizedPageSizeOptions = React.useMemo(() => {
    if (!pageSizeOptions?.includes(10)) {
      return [10, ...(pageSizeOptions || [])]
    }
    return pageSizeOptions
  }, [pageSizeOptions])

  // Handler for filter model change
  const handleFilterModelChange = props.onFilterModelChange || (() => { });

  return (
    <Box className="common-data-grid-container">
      {
        !hideHeaderCount ? <Box className="common-data-grid-header">
          <Box className="common-data-grid-header-left">
            {enableSearch && (
              <TextField
                placeholder={searchPlaceholderText}
                value={searchValue !== undefined ? searchValue : searchTerm}
                className="common-data-grid-header-search"
                onChange={(e) => {
                  if (onSearchChange) {
                    onSearchChange(e.target.value);
                  } else {
                    setSearchTerm(e.target.value);
                  }
                }}
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon className="common-data-grid-header-search-icon" />
                    </InputAdornment>
                  ),
                }}
              />
            )}
            {effectiveRowCount > 0 && (
              <Typography
                variant="body2"
                className="common-data-grid-header-row-count"
              >
                {paginationModel.page * paginationModel.pageSize + 1} -{" "}
                {Math.min(
                  (paginationModel.page + 1) * paginationModel.pageSize,
                  effectiveRowCount
                )}{" "}
                {t("of", "of")} {totalRowCount} {pageName}
              </Typography>
            )}
          </Box>
          {/* Show Per Page Selector */}
          <Box className="common-data-grid-header-right">
          <Typography className="common-data-grid-header-right-label-show hide-mobile">
            {t("show", "Show:")}
          </Typography>
          <FormControl
            size="small"
            className="common-data-grid-header-right-formcontrol"
          >
            <Select
              value={
                paginationModel?.pageSize !== undefined
                  ? paginationModel.pageSize
                  : 10
              }
              onChange={handlePageSizeChange}
              displayEmpty
              className="common-data-grid-header-right-select"
              inputProps={{ "aria-label": "Show Per Page" }}
            >
              {normalizedPageSizeOptions.map((size) => (
                <MuiMenuItem
                  key={size}
                  value={size}
                  className="common-data-grid-header-right-menuitem"
                >
                  {size} {t("perPage", "PER PAGE")}
                </MuiMenuItem>
              ))}
            </Select>
          </FormControl>
          {customActionEvent && (
            <Button
              variant="contained"
              disabled={selectedCartCount === 0}
              endIcon={<ArrowForwardIosIcon style={{ fontSize: 14 }} />}
              className={`common-data-grid-header-right-button ${selectedCartCount === 0 ? "disabled" : ""
                }`}
              onClick={customActionEvent}
            >
              {customActionEventTitle}{" "}
              {selectedCartCount > 0 ? `(${selectedCartCount})` : ""}
            </Button>
          )}
            {showCustomActionEventLoader && <MiniLoader />}
        </Box>
        </Box> : null
      }

      <Box sx={{ width: '100%', overflowX: 'auto', overflowY: 'hidden' }}>
        <DataGrid
          sortingMode={sortingMode}
          onSortModelChange={(model) => {
            if (props.onSortChange) {
              if (model && model.length > 0) {
                const { field, sort } = model[0];
                props.onSortChange(field, sort ?? null);
              } else {
                props.onSortChange(null, null);
              }
            }
          }}
          sx={{
            maxWidth: '100%',
            '& .MuiCheckbox-root': {
              border: 'none !important',
              boxShadow: 'none !important',
              outline: 'none !important',
            },
            '& .MuiCheckbox-root:focus, & .MuiCheckbox-root:focus-visible, & .MuiCheckbox-root.Mui-focusVisible': {
              border: 'none !important',
              boxShadow: 'none !important',
              outline: 'none !important',
            },
            '& .MuiCheckbox-root.Mui-checked': {
              color: 'var(--primary) !important',
            },
            '& .MuiDataGrid-virtualScroller': {
              overflowY: 'auto !important',
              overflowX: 'auto !important',
            },
            "& .MuiDataGrid-cell": {
              display: "flex",
              alignItems: "center",
              whiteSpace: "normal",
              lineHeight: "normal",
              padding: "0 !important",
            },
            "& .MuiDataGrid-columnHeader": {
              padding: "0 !important",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              padding: "0 !important",
            },
            "& .MuiDataGrid-row": {
              maxHeight: "none !important",
            },
          }}
          getRowHeight={() => 'auto'}
          columns={enhancedColumns}
          rows={processRowsWithChildren(displayRows)}
          checkboxSelection={checkboxSelection}
          disableRowSelectionOnClick
          pageSizeOptions={pageSizeOptions}
          paginationMode={paginationMode}
          paginationModel={paginationModel}
          loading={loading}
          {...(paginationMode === 'server' ? { rowCount: effectiveRowCount } : {})}
          hideFooter={hideFooter}
          onRowSelectionModelChange={(selectedRows, details) => {
            if (onRowSelect) {
              onRowSelect(selectedRows, details);
            }
          }}
          onPaginationModelChange={onPaginationModelChange}
          getRowId={(row: any) => row.id || row._id || row.key || row.code || row.order}
          localeText={{
            noRowsLabel: noRowsMessage || t("noRows", "No rows"),
            noResultsOverlayLabel: noRowsMessage || t("noRows", "No rows"),
          }}
          className="common-data-grid-datagrid"
          slotProps={{
            loadingOverlay: {
              variant: 'linear-progress',
            }
          }}
          disableColumnSelector
          onFilterModelChange={handleFilterModelChange}
        />
      </Box>
      <Box className="common-data-grid-bottom-button-container">
        {customActionEvent && (
          <Button
            variant="contained"
            disabled={selectedCartCount === 0}
            endIcon={<ArrowForwardIosIcon style={{ fontSize: 14 }} />}
            className={`common-data-grid-bottom-button ${selectedCartCount === 0 ? "disabled" : ""
              }`}
            onClick={customActionEvent}
          >
            {customActionEventTitle} {selectedCartCount > 0 ? `(${selectedCartCount})` : ""}
          </Button>
        )}
      </Box>
      {!hideFooter && (
        <Box>
          <CustomPagination
            page={paginationModel?.page || 0}
            pageSize={paginationModel?.pageSize || 100}
            rowCount={effectiveRowCount}
            totalRowCount={totalRowCount}
            onChange={onPaginationModelChange}
            hideFooterEntries={hideFooterEntries}
          />
        </Box>
      )}
    </Box>
  );
};

export default CommonDataGrid;

// Custom Pagination Component
interface CustomPaginationProps {
  page: number;
  pageSize: number;
  rowCount: number;
  totalRowCount?: number;
  onChange?: (model: { page: number; pageSize: number }, details: any) => void;
  hideFooterEntries?: boolean;
}

const CustomPagination: React.FC<CustomPaginationProps> = ({
  page,
  pageSize,
  rowCount,
  totalRowCount,
  onChange,
  hideFooterEntries = false,
}) => {
  // Use totalRowCount for server pagination, fallback to rowCount for client pagination
  const effectiveRowCount = totalRowCount || rowCount;
  const totalPages = Math.max(1, Math.ceil(effectiveRowCount / pageSize));
  const maxVisible = 7; // Show up to 7 page numbers
  let start = Math.max(1, page + 1 - 3);
  let end = Math.min(totalPages, start + maxVisible - 1);
  if (end - start < maxVisible - 1) {
    start = Math.max(1, end - maxVisible + 1);
  }
  const pages: (number | "ellipsis")[] = [];
  if (start > 1) {
    pages.push(1);
    if (start > 2) pages.push("ellipsis");
  }
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  if (end < totalPages) {
    if (end < totalPages - 1) pages.push("ellipsis");
    pages.push(totalPages);
  }

  return (
    <Grid
      container
      spacing={2}
      alignItems="center"
      mt={2}
      className="custom-pagination-container"
    >
      {/* Pagination - Centered across entire container */}
      <Grid size={12}>
        <Box className="custom-pagination-main">
          {/* Previous Arrow */}
          <Box
            className={`custom-pagination-prev ${page === 0 ? "disabled" : ""}`}
            onClick={() => {
              if (onChange && page > 0) {
                onChange({ page: page - 1, pageSize }, { reason: "prevPage" });
              }
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 18L9 12L15 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Box>

          {pages.map((p, idx) =>
            p === "ellipsis" ? (
              <Typography
                key={`ellipsis-${idx}`}
                className="custom-pagination-ellipsis"
              >
                …
              </Typography>
            ) : (
              <Box
                key={`page-${p}`}
                className={`custom-pagination-page ${page + 1 === p ? "active" : ""
                  }`}
                onClick={() => {
                  if (onChange && page + 1 !== p && typeof p === "number") {
                    onChange(
                      { page: p - 1, pageSize },
                      { reason: "pageChange" }
                    );
                  }
                }}
              >
                {p}
              </Box>
            )
          )}

          {/* Next Arrow */}
          <Box
            className={`custom-pagination-next ${page + 1 === totalPages ? "disabled" : ""
              }`}
            onClick={() => {
              if (onChange && page + 1 < totalPages) {
                onChange({ page: page + 1, pageSize }, { reason: "nextPage" });
              }
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 6L15 12L9 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Box>
        </Box>
      </Grid>

      {/* Pagination results summary - Right aligned by default, responsive for mobile */}
      {effectiveRowCount > 0 && !hideFooterEntries && (
        <Box className="custom-pagination-results-desktop pagination-results-desktop-responsive">
          <Typography variant="body2">
            {page * pageSize + 1} - {Math.min((page + 1) * pageSize, effectiveRowCount)}{" "}
            {t("of", "of")} {effectiveRowCount} {t("results", "results")}
          </Typography>
        </Box>
      )}

      {/* Results count for mobile - centered below pagination */}
      {effectiveRowCount > 0 && !hideFooterEntries && (
        <Grid size={12} className="pagination-results-mobile-responsive">
          <Box className="custom-pagination-results-mobile">
            <Typography variant="body2">
              {t("showing", "Showing")} {page * pageSize + 1} {t("to", "to")}{" "}
              {Math.min((page + 1) * pageSize, effectiveRowCount)} {t("of", "of")} {effectiveRowCount} {t("entries", "entries")}
            </Typography>
          </Box>
        </Grid>
      )}
    </Grid>
  );
};
